// Pipeline → Lease Calculator popup: quick lease economics and tenant-rep / landlord-rep
// commissions for any lease, without creating a deal. Inputs are remembered on this device.

import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FeeTotals } from '../lib/commission';
import { money } from '../lib/format';
import { emptyCalc, groupSchedule, runCalculator, SIDE_PRESETS, type CalcComponent, type CalcSide, type CalcState } from '../lib/leaseCalculator';
import { round2 } from '../lib/money';
import { Button, cx, Field, Hint, Input, Select } from './ui';

const STORAGE_KEY = 'lease-calculator-v1';
const BASIS_LABEL: Record<CalcComponent['basis'], string> = {
  months_rent: "months' rent",
  pct_lease_value: '% of lease value',
  flat: '$ flat',
  per_sf: '$ per SF',
};
const UNIT_LABEL = { monthly: '$ / month', psf_year: '$ / SF / yr', pct_increase: '% increase' } as const;

function load(): CalcState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { ...emptyCalc(), ...(JSON.parse(saved) as Partial<CalcState>) };
  } catch {
    // Storage blocked or bad JSON: start fresh.
  }
  return emptyCalc();
}

function Toggle<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex rounded-md bg-slate-100 p-0.5">
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={cx('rounded px-2.5 py-1 text-xs font-medium', value === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function LeaseCalculator() {
  const [c, setC] = useState<CalcState>(load);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    } catch {
      // Not saved; the calculator still works.
    }
  }, [c]);
  const update = (fn: (draft: CalcState) => void) =>
    setC((prev) => {
      const next = structuredClone(prev);
      fn(next);
      return next;
    });
  const set = (k: 'termMonths' | 'sf' | 'baseRent' | 'freeMonths' | 'freeCustom' | 'opex' | 'escalationPct' | 'thereafterPct') => (e: { target: { value: string } }) =>
    update((x) => void (x[k] = e.target.value));
  const r = useMemo(() => runCalculator(c), [c]);
  const lease = r.lease;

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      {/* Inputs */}
      <div className="space-y-4 lg:col-span-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Lease term (months)">
            <Input inputMode="numeric" value={c.termMonths} onChange={set('termMonths')} placeholder="60" />
          </Field>
          <Field label="Suite SF">
            <Input inputMode="numeric" value={c.sf} onChange={set('sf')} placeholder="3,000" />
          </Field>
          <Field label="Base rent ($/SF/yr)">
            <Input inputMode="decimal" value={c.baseRent} onChange={set('baseRent')} placeholder="30.00" />
          </Field>
        </div>

        <div>
          <div className="mb-1.5 flex items-center gap-3">
            <span className="text-xs font-medium text-slate-600">Free rent?</span>
            <Toggle
              value={c.freeRent}
              options={[
                ['none', 'None'],
                ['start', 'At start of term'],
                ['custom', 'Custom months'],
              ]}
              onChange={(v) => update((x) => void (x.freeRent = v))}
            />
          </div>
          {c.freeRent === 'start' && (
            <div className="flex items-center gap-2 text-sm">
              <Input className="!w-20" inputMode="numeric" value={c.freeMonths} onChange={set('freeMonths')} placeholder="3" /> months free, starting month 1
            </div>
          )}
          {c.freeRent === 'custom' && (
            <Field label="Free months" hint="Month numbers in the term, e.g. 1, 13, 16 — or a range like 1-3, 13">
              <Input value={c.freeCustom} onChange={set('freeCustom')} placeholder="1, 13, 16" />
            </Field>
          )}
        </div>

        <Field label="Estimated OPEX ($/SF/yr)" hint="Only add if you're getting paid on OPEX.">
          <Input className="!w-32" inputMode="decimal" value={c.opex} onChange={set('opex')} placeholder="optional" />
        </Field>

        <div>
          <div className="mb-1.5 flex items-center gap-3">
            <span className="text-xs font-medium text-slate-600">Annual escalations</span>
            <Toggle
              value={c.escalation}
              options={[
                ['percent', 'Percentage'],
                ['custom', 'Custom'],
              ]}
              onChange={(v) =>
                update((x) => {
                  x.escalation = v;
                  if (v === 'custom' && x.steps.length === 0) x.steps = [{ from: '1', to: '12', unit: 'monthly', value: '' }];
                })
              }
            />
          </div>
          {c.escalation === 'percent' ? (
            <div className="flex items-center gap-2 text-sm">
              <Input className="!w-20" inputMode="decimal" value={c.escalationPct} onChange={set('escalationPct')} />% every year
            </div>
          ) : (
            <div className="space-y-2 rounded-md border border-slate-200 p-2.5">
              {c.steps.map((st, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                  Months
                  <Input className="!w-14" inputMode="numeric" value={st.from} onChange={(e) => update((x) => void (x.steps[i]!.from = e.target.value))} />–
                  <Input className="!w-14" inputMode="numeric" value={st.to} onChange={(e) => update((x) => void (x.steps[i]!.to = e.target.value))} />
                  <Input className="!w-24" inputMode="decimal" value={st.value} onChange={(e) => update((x) => void (x.steps[i]!.value = e.target.value))} />
                  <Select className="!w-32" value={st.unit} onChange={(e) => update((x) => void (x.steps[i]!.unit = e.target.value as typeof st.unit))}>
                    {Object.entries(UNIT_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                  <button className="text-slate-400 hover:text-rose-600" onClick={() => update((x) => void x.steps.splice(i, 1))} aria-label="Remove step">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  update((x) => {
                    const last = Number(x.steps[x.steps.length - 1]?.to) || 0;
                    x.steps.push({ from: String(last + 1), to: String(last + 12), unit: 'monthly', value: '' });
                  })
                }
              >
                <Plus className="h-3.5 w-3.5" /> Step
              </Button>
              <div className="flex items-center gap-2 border-t border-slate-100 pt-2 text-sm">
                Then
                <Input className="!w-20" inputMode="decimal" value={c.thereafterPct} onChange={set('thereafterPct')} />% annually thereafter
              </div>
              <p className="text-xs text-slate-400">Months before the first step use the base rent. “% increase” raises the rent from the step before it.</p>
            </div>
          )}
        </div>

        <SideEditor title="Tenant rep commission" side={c.tenantRep} presets={SIDE_PRESETS.tenantRep} onChange={(s) => update((x) => void (x.tenantRep = s))} />
        <SideEditor title="Landlord rep commission" side={c.landlordRep} presets={SIDE_PRESETS.landlordRep} onChange={(s) => update((x) => void (x.landlordRep = s))} />

        <button className="text-xs text-slate-400 hover:text-slate-700 hover:underline" onClick={() => setC(emptyCalc())}>
          Clear all
        </button>
      </div>

      {/* Results */}
      <div className="space-y-3 lg:col-span-2">
        {r.error ? (
          <Hint tone="gray">{r.error}</Hint>
        ) : (
          lease && (
            <>
              <div className="rounded-lg bg-indigo-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-indigo-700">Total potential commission</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{money(r.total)}</div>
                <div className="mt-2 space-y-0.5 text-sm">
                  {r.tenantRep && <Line label="Tenant rep" value={r.tenantRep.gross} />}
                  {r.landlordRep && <Line label="Landlord rep" value={r.landlordRep.gross} />}
                </div>
              </div>
              <dl className="space-y-1 rounded-lg border border-slate-200 p-3 text-sm">
                <Line label="Aggregate base rent" value={lease.aggregateBaseRent} />
                {lease.abatedRent > 0 && <Line label="Free rent excluded" value={lease.abatedRent} muted />}
                {lease.opexEnabled && <Line label="Aggregate OPEX" value={lease.aggregateOpex} />}
                <Line label="Aggregate lease value" value={lease.aggregateLeaseValue} strong />
                <Line label={`Monthly rent for commission${lease.firstFullMonth && lease.firstFullMonth > 1 ? ` (month ${lease.firstFullMonth})` : ''}`} value={lease.commissionMonthlyRent} />
                <Line label="Net effective rent $/SF/yr" value={round2((lease.aggregateBaseRent / lease.termMonths) * 12 / lease.rentableSf)} />
              </dl>
              <Breakdown title="Tenant rep" fees={r.tenantRep} side={c.tenantRep} />
              <Breakdown title="Landlord rep" fees={r.landlordRep} side={c.landlordRep} />
              <details className="rounded-lg border border-slate-200 p-3 text-sm">
                <summary className="cursor-pointer text-xs font-medium text-slate-600">Rent schedule</summary>
                <table className="mt-2 w-full text-xs">
                  <thead className="text-left text-slate-500">
                    <tr>
                      <th className="font-medium">Months</th>
                      <th className="text-right font-medium">$/month</th>
                      <th className="text-right font-medium">$/SF/yr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupSchedule(lease).map((g) => (
                      <tr key={g.from} className={cx(g.free && 'text-slate-400')}>
                        <td className="py-0.5">
                          {g.from === g.to ? g.from : `${g.from}–${g.to}`}
                          {g.free && ' (free)'}
                        </td>
                        <td className="text-right">{money(g.monthlyRent)}</td>
                        <td className="text-right">{money(g.psfYear)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </>
          )
        )}
      </div>
    </div>
  );
}

function Line({ label, value, strong, muted }: { label: string; value: number | null; strong?: boolean; muted?: boolean }) {
  return (
    <div className={cx('flex justify-between gap-3', muted && 'text-slate-500')}>
      <span className="text-slate-600">{label}</span>
      <span className={cx(strong ? 'font-semibold text-slate-900' : 'font-medium')}>{value === null ? '—' : money(round2(value))}</span>
    </div>
  );
}

function Breakdown({ title, fees, side }: { title: string; fees: FeeTotals | null; side: CalcSide }) {
  if (!fees) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm">
      <div className="mb-1 text-xs font-medium text-slate-600">{title}</div>
      {fees.components.map((f, i) => {
        const comp = side.components[i]!;
        return <Line key={f.key} label={`${comp.rate || '?'} ${BASIS_LABEL[comp.basis]}`} value={f.amount} />;
      })}
    </div>
  );
}

function SideEditor({ title, side, presets, onChange }: { title: string; side: CalcSide; presets: { label: string; components: CalcComponent[] }[]; onChange: (s: CalcSide) => void }) {
  const edit = (fn: (s: CalcSide) => void) => {
    const next = structuredClone(side);
    fn(next);
    onChange(next);
  };
  return (
    <div className={cx('rounded-md border border-slate-200 p-2.5', !side.enabled && 'opacity-60')}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
          <input type="checkbox" checked={side.enabled} onChange={(e) => edit((s) => void (s.enabled = e.target.checked))} /> {title}
        </label>
        <div className="ml-auto flex flex-wrap gap-1">
          {presets.map((p) => (
            <Button key={p.label} size="sm" variant="ghost" onClick={() => edit((s) => void (s.components = structuredClone(p.components)))}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      {side.enabled && (
        <div className="space-y-1.5">
          {side.components.map((comp, i) => (
            <div key={i} className="flex items-center gap-1.5 text-sm">
              {i > 0 && <span className="text-slate-400">+</span>}
              <Input className="!w-20" inputMode="decimal" value={comp.rate} onChange={(e) => edit((s) => void (s.components[i]!.rate = e.target.value))} />
              <Select className="!w-44" value={comp.basis} onChange={(e) => edit((s) => void (s.components[i]!.basis = e.target.value as CalcComponent['basis']))}>
                {Object.entries(BASIS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
              <button className="text-slate-400 hover:text-rose-600" onClick={() => edit((s) => void s.components.splice(i, 1))} aria-label="Remove">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => edit((s) => void s.components.push({ basis: 'pct_lease_value', rate: '' }))}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
